import React from 'react';
import { User } from 'firebase/auth';
import DocumentThumbnail from './DocumentThumbnail';

// What document-scan-service records on a file doc under `scan`.
export interface DocumentScan {
  status: 'requested' | 'cleaned' | 'rejected' | 'failed' | string;
  gcsPath?: string | null;
  method?: string;
  uvdocApplied?: boolean;
  paperCondition?: string;
  verification?: { accept?: boolean | null; tokens_kept?: number; drift_cer?: number; reason?: string };
  reason?: string;
  version?: string;
}

interface DocumentScanRowProps {
  caseId: string;
  documentId: string;
  fileName: string;
  fileType?: string;
  scan: DocumentScan;
  user: User | null;
  onOpen: () => void;
}

// Identity documents (the holder's passport, a guardian's passport) are cut out of the photo pixel for pixel:
// nothing is redrawn. Proof-of-address and birth-certificate pages are re-rendered.
export const IDENTITY_FILE_TYPES = new Set(['proof_of_identity', 'guardian_identity']);

// The scan's progress only. A cleaned page carries no verdict on its text: the lawyer compares it with the
// original and decides, so a "text check failed" label would only be noise.
export const scanBadge = (scan: DocumentScan, fileType?: string): { label: string; tone: 'ok' | 'warn' | 'muted' } | null => {
  if (scan.status === 'requested') return { label: 'Cleaning...', tone: 'muted' };
  if (scan.status === 'failed') return { label: 'Cleanup failed', tone: 'muted' };
  if (fileType && IDENTITY_FILE_TYPES.has(fileType)) {
    if (scan.status === 'cleaned') return { label: 'Original pixels, background removed', tone: 'ok' };
    return { label: 'Could not outline the document', tone: 'warn' };
  }
  return null;
};

const DocumentScanRow: React.FC<DocumentScanRowProps> = ({ caseId, documentId, fileName, fileType, scan, user, onOpen }) => {
  const identity = !!fileType && IDENTITY_FILE_TYPES.has(fileType);
  const badge = scanBadge(scan, fileType);
  const hasPage = (scan.status === 'cleaned' || scan.status === 'rejected') && !!scan.gcsPath;
  return (
    <div className="document-item document-scan-item" data-testid={`document-scan-${documentId}`}>
      {hasPage ? (
        <DocumentThumbnail
          caseId={caseId}
          documentId={documentId}
          fileName={`cleaned page of ${fileName}`}
          user={user}
          onOpen={onOpen}
          variant="scan"
        />
      ) : (
        <div className="document-thumbnail document-thumbnail-unavailable" aria-hidden="true">
          <span className="document-thumbnail-fallback">✨</span>
        </div>
      )}
      <div className="document-info">
        <div className="document-name">
          {identity ? 'Cleaned passport' : 'Cleaned scan'} <span className="doc-side-badge scan-beta-badge">beta</span>
        </div>
        <div className="document-type">
          {identity
            ? `Cut out of ${fileName}. The original stays in this list; the PDF export uses this copy.`
            : `Derived from ${fileName}. Preview only: the original stays the document of record and the download package does not include it.`}
          {badge && <span className={`scan-badge scan-badge-${badge.tone}`}>{badge.label}</span>}
        </div>
      </div>
      {hasPage && (
        <button className="view-btn" onClick={onOpen}>View</button>
      )}
    </div>
  );
};

export default DocumentScanRow;
